<template>
  <div class="rp-overview">
    <!-- Rounds: every child project is a node; planned rounds come from the schedule -->
    <div class="pm-card">
      <div class="pm-card__head">
        <h3>{{ $t('projects.recurring.rounds') }}</h3>
        <span class="pm-card__sub">{{ $t('projects.recurring.rounds_sub') }}</span>
        <span class="grow"></span>
        <span class="pm-link"
              @click="$emit('open-tab', 'rounds')"
              v-if="rounds.length"
              qid="recurring-all-rounds">{{ $t('projects.recurring.all_rounds', {count: rounds.length}) }}</span>
      </div>
      <div class="pm-empty" v-if="loading"><font-awesome-icon icon="spinner" class="fa-spin mr-2"/>{{ $t('projects.management.loading') }}</div>
      <div class="pm-empty" v-if="!loading && !nodes.length">{{ $t('projects.recurring.no_rounds') }}</div>
      <div class="rp-rounds" v-if="!loading && nodes.length">
        <div class="rp-rounds__track" :style="trackStyle"><i :style="{width: trackDone + '%'}"></i></div>
        <div class="rp-rounds__nodes" :style="{gridTemplateColumns: 'repeat(' + nodes.length + ', minmax(0, 1fr))'}">
          <component :is="node.slug ? 'router-link' : 'div'"
                     v-for="(node, index) in nodes"
                     :key="index"
                     :to="node.slug ? {name: 'projects.view', params: {slug: node.slug}} : undefined"
                     class="rp-node"
                     :class="'rp-node--' + node.state"
                     qid="recurring-round-node"
          >
            <span class="rp-node__date">{{ node.date }}</span>
            <span class="rp-node__dot">
              <template v-if="node.state === 'plan'"><font-awesome-icon icon="calendar-alt"/></template>
              <template v-else-if="node.state === 'due'">!</template>
              <template v-else>{{ node.progress }}%</template>
            </span>
            <span class="rp-node__title">{{ node.title }}</span>
            <span class="rp-node__text">{{ node.text }}</span>
          </component>
        </div>
      </div>
    </div>

    <div class="pm-cols pm-cols--wide">
      <!-- Everything a new round copies from this project -->
      <div class="pm-card">
        <div class="pm-card__head">
          <h3>{{ $t('projects.recurring.base_info') }}</h3>
          <span class="pm-card__sub">{{ $t('projects.recurring.base_info_sub') }}</span>
          <span class="grow"></span>
          <span class="pm-tag pm-tag--grey"><font-awesome-icon icon="arrow-down" class="mr-1"/>{{ $t('projects.recurring.applies_next_round') }}</span>
        </div>
        <div class="rp-base">
          <div class="rp-base__card"
               v-for="card in baseCards"
               :key="card.key"
               @click="$emit('open-tab', card.tab)"
               :qid="'recurring-base-' + card.key">
            <span class="rp-base__inherited"><font-awesome-icon icon="arrow-down"/>{{ $t('projects.recurring.inherited') }}</span>
            <div class="rp-base__head"><font-awesome-icon :icon="card.icon"/>{{ $t('projects.recurring.cards.' + card.key) }}<font-awesome-icon icon="pen" class="rp-base__edit"/></div>
            <div class="rp-base__value" v-if="card.users && card.users.length"><Team :users="card.users"/>{{ card.value }}</div>
            <div class="rp-base__value" v-else :class="{'rp-base__value--quote': card.quote, 'text-muted': card.empty}">{{ card.value }}</div>
            <div class="rp-base__text" v-if="card.text">{{ card.text }}</div>
          </div>
        </div>
      </div>

      <div class="pm-card">
        <div class="pm-card__head"><h3>{{ $t('projects.recurring.across_rounds') }}</h3></div>
        <div class="rp-kpis">
          <div class="rp-kpi"><b>{{ rounds.length }}</b><span>{{ $t('projects.recurring.kpi.rounds') }}</span></div>
          <div class="rp-kpi"><b>{{ finished.length ? onTime + ' / ' + finished.length : '—' }}</b><span>{{ $t('projects.recurring.kpi.on_time') }}</span></div>
          <div class="rp-kpi"><b>{{ averageDays !== null ? $tc('projects.recurring.days_value', averageDays, {count: averageDays}) : '—' }}</b><span>{{ $t('projects.recurring.kpi.average') }}</span></div>
          <div class="rp-kpi"><b>{{ current ? current.progress + '%' : '—' }}</b><span>{{ $t('projects.recurring.kpi.current') }}</span></div>
        </div>
        <div class="rp-bars" v-if="rounds.length">
          <div v-for="(round, index) in rounds.slice(-8)"
               :key="index"
               v-b-tooltip.hover
               :title="round.title + ': ' + (round.progress || 0) + '%'">
            <i :class="{'cur': isRunning(round)}" :style="{height: Math.max(4, (round.progress || 0)) + '%'}"></i>
            <span>{{ roundLabel(round) }}</span>
          </div>
        </div>
        <div class="rp-bars__caption" v-if="rounds.length">{{ $t('projects.recurring.bars_caption') }}</div>
        <div class="pm-card__foot">
          <span class="pm-link" @click="$emit('open-tab', 'history')" qid="recurring-history">{{ $t('projects.recurring.see_history') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import { runDates } from '@/views/Projects/Management/projectTypes.js'
  import Team from '@/views/Projects/Management/Team.vue';

  // Overview tab of a recurring project: its rounds, what they inherit and how they went (US 9396)
  export default {
    name: 'RecurringProjectOverview',
    components: {
      Team
    },
    props: {
      project: {type: Object, required: true}
    },
    data: function() {
      return {
        loading: true,
        rounds: []
      }
    },
    computed: {
      finished() {
        return _.filter(this.rounds, function(round) {
          return round.status === 'completed' || round.status === 'closed';
        });
      },
      onTime() {
        var moment = require('moment');
        return _.filter(this.finished, function(round) {
          let done = round.completed_at || round.closed_at;
          return done && round.end_date && !moment(done).startOf('day').isAfter(moment(round.end_date).endOf('day'));
        }).length;
      },
      averageDays() {
        var moment = require('moment');
        let durations = _.compact(_.map(this.finished, function(round) {
          let done = round.completed_at || round.closed_at;
          return done && round.start_date ? Math.max(1, moment(done).diff(moment(round.start_date), 'days')) : null;
        }));
        return durations.length ? Math.round(_.sum(durations) / durations.length) : null;
      },
      current() {
        let self = this;
        return _.findLast(this.rounds, function(round) {
          return self.isRunning(round);
        });
      },
      // The last four rounds, a due round and the next two planned ones
      nodes() {
        let self = this;
        var moment = require('moment');
        let nodes = _.map(this.rounds.slice(-4), function(round, index, list) {
          let number = self.rounds.length - list.length + index + 1;
          let state = self.isRunning(round) ? 'run' : 'done';
          let text = state === 'run'
            ? (round.end_date ? self.$t('projects.recurring.running_due', {date: moment(round.end_date).format('D MMM')}) : self.$t('projects.launcher.running_now'))
            : self.$t('projects.recurring.closed');
          return {slug: round.slug, state: state, progress: round.progress || 0, date: self.roundLabel(round, true),
            title: self.$t('projects.recurring.round_number', {number: number}), text: text};
        });
        if (this.project.run_due) {
          nodes.push({state: 'due', date: moment().format('MMM YYYY'), title: this.$t('projects.recurring.round_number', {number: this.rounds.length + 1}), text: this.$t('projects.launcher.due_now')});
        }
        let planned = runDates(this.project, moment().add(1, 'day'), 2);
        _.forEach(planned, function(date) {
          nodes.push({state: 'plan', date: date.format('MMM YYYY'), title: date.format('D MMM YYYY'),
            text: self.project.auto_create ? self.$t('projects.management.planned_auto') : self.$t('projects.management.planned_manual')});
        });
        return nodes;
      },
      trackDone() {
        let done = _.filter(this.nodes, function(node) {
          return node.state === 'done' || node.state === 'run';
        }).length;
        return this.nodes.length > 1 ? Math.min(100, Math.max(0, (done - 0.5) / (this.nodes.length - 1) * 100)) : 0;
      },
      // The line runs from the centre of the first node to the centre of the last one
      trackStyle() {
        let inset = 'calc(22px + (100% - 44px) / ' + (this.nodes.length * 2) + ')';
        return {left: inset, right: inset};
      },
      baseCards() {
        let project = this.project;
        let cards = [];
        if (project.scope) {
          cards.push({key: 'scope', icon: 'th-large', tab: 'scope', value: this.$t('projects.launcher.scopes.' + project.scope),
            text: this.assigneeText(project.scope_owners, project.scope_members)});
        }
        let assignees = project.scopeUsers || project.scope_users || [];
        cards.push({key: 'assignees', icon: 'users', tab: project.scope ? 'scope' : 'base', users: assignees,
          value: assignees.length ? this.$tc('projects.recurring.people', assignees.length, {count: assignees.length}) : this.$t('projects.recurring.no_default_assignees'),
          empty: !assignees.length});
        let users = project.users || [];
        cards.push({key: 'contributors', icon: 'user-cog', tab: 'base', users: users,
          value: this.$tc('projects.recurring.people', users.length, {count: users.length}), text: this.$t('projects.recurring.contributors_text')});
        let description = project.description ? project.description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '';
        cards.push({key: 'description', icon: 'align-left', tab: 'schedule', quote: !!description, empty: !description,
          value: description ? _.truncate(description, {length: 110}) : this.$t('system.missing_input')});
        // Assessments don't send a message or attachments to assignees
        let assessment = ['control_assessment', 'risk_assessment', 'bia_assessment'].indexOf(project.type) !== -1;
        if (!assessment) {
          cards.push({key: 'message', icon: 'comment', tab: 'schedule', quote: !!project.schedule_message, empty: !project.schedule_message,
            value: project.schedule_message ? _.truncate(project.schedule_message, {length: 110}) : this.$t('system.missing_input')});
          let files = _.filter(project.media || [], function(media) {
            return media.collection_name === 'documents' || media.collection_name === 'project';
          });
          let links = _.filter(project.links || [], function(link) {
            return link.collection == null;
          });
          cards.push({key: 'attachments', icon: 'paperclip', tab: 'schedule', empty: !files.length && !links.length,
            value: this.$tc('projects.recurring.files', files.length, {count: files.length}) + ' · ' + this.$tc('projects.recurring.links', links.length, {count: links.length}),
            text: _.map(files.slice(0, 2), 'file_name').join(', ')});
        }
        let tags = _.map(project.tags_translated || project.tagsTranslated || [], 'name_translated');
        cards.push({key: 'settings', icon: 'cog', tab: 'base',
          value: project.overdue_notifications ? this.$t('projects.engagement.overview.reminders_on') : this.$t('projects.engagement.overview.reminders_off'),
          text: tags.length ? tags.join(', ') : ''});
        return cards;
      }
    },
    methods: {
      isRunning(round) {
        return round.status !== 'completed' && round.status !== 'closed';
      },
      roundLabel(round, long) {
        var moment = require('moment');
        if (!round.start_date) {
          return '—';
        }
        return moment(round.start_date).format(long ? 'MMM YYYY' : 'MMM YY');
      },
      assigneeText(owners, members) {
        if (owners && members) {
          return this.$t('projects.recurring.owners_and_members');
        }
        if (owners) {
          return this.$t('projects.recurring.owners');
        }
        if (members) {
          return this.$t('projects.recurring.members');
        }
        return '';
      },
      loadRounds() {
        this.loading = true;
        piincHttp.get('projects/' + this.project.slug + '/projects', {params: {per_page: 100, sort: 'start_date', order: 'asc'}}).then((response) => {
          this.rounds = response.data.items;
          this.loading = false;
        }, () => {
          this.loading = false;
        });
      }
    },
    created() {
      this.loadRounds();
    }
  }
</script>
